import { Extension } from '@tiptap/core';
import Suggestion from '@tiptap/suggestion';
import { ReactRenderer } from '@tiptap/react';
import React from 'react';
import { createPortal } from 'react-dom';
import { 
  Heading1, 
  Heading2, 
  Heading3, 
  List, 
  ListOrdered, 
  Quote, 
  Code,
  FileText,
  BookOpen,
} from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SlashCommandItem {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  command: (props: { editor: any; range: any }) => void;
}

// This will be set by the editor component
let insertReferenceCallback: (() => void) | null = null;

export const setInsertReferenceCallback = (callback: () => void) => {
  insertReferenceCallback = callback;
};

const getSuggestionItems = ({ query }: { query: string }): SlashCommandItem[] => {
  const items: SlashCommandItem[] = [
    {
      title: 'Reference Block',
      description: 'Insert a reference to a guide or article',
      icon: BookOpen,
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .run();
        if (insertReferenceCallback) {
          insertReferenceCallback();
        }
      },
    },
    {
      title: 'Heading 1',
      description: 'Big section heading',
      icon: Heading1,
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setNode('heading', { level: 1 })
          .run();
      },
    },
    {
      title: 'Heading 2',
      description: 'Medium section heading',
      icon: Heading2,
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setNode('heading', { level: 2 })
          .run();
      },
    },
    {
      title: 'Heading 3',
      description: 'Small section heading',
      icon: Heading3,
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setNode('heading', { level: 3 })
          .run();
      },
    },
    {
      title: 'Bullet List',
      description: 'Create a bullet list',
      icon: List,
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .toggleBulletList()
          .run();
      },
    },
    {
      title: 'Numbered List',
      description: 'Create a numbered list',
      icon: ListOrdered,
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .toggleOrderedList()
          .run();
      },
    },
    {
      title: 'Quote',
      description: 'Create a quote block',
      icon: Quote,
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .toggleBlockquote()
          .run();
      },
    },
    {
      title: 'Code Block',
      description: 'Create a code block',
      icon: Code,
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .toggleCodeBlock()
          .run();
      },
    },
    {
      title: 'Paragraph',
      description: 'Plain text paragraph',
      icon: FileText,
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setParagraph()
          .run();
      },
    },
  ];

  return items.filter(item => 
    item.title.toLowerCase().includes(query.toLowerCase()) ||
    item.description.toLowerCase().includes(query.toLowerCase())
  );
};

interface SlashCommandListProps {
  items: SlashCommandItem[];
  command: (item: SlashCommandItem) => void;
  selectedIndex: number;
}

interface SlashCommandListContainerProps extends SlashCommandListProps {
  clientRect: (() => DOMRect | null) | null;
}

const SlashCommandList = React.forwardRef<any, SlashCommandListProps>(
  ({ items, command, selectedIndex }, ref) => {
    React.useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: { event: KeyboardEvent }) => {
        if (event.key === 'ArrowUp') {
          return true;
        }
        if (event.key === 'ArrowDown') {
          return true;
        }
        if (event.key === 'Enter') {
          if (items[selectedIndex]) {
            command(items[selectedIndex]);
            return true;
          }
        }
        return false;
      },
    }));

    return (
      <div className="z-50 min-w-[280px] rounded-md border bg-popover p-1 text-popover-foreground shadow-md outline-none animate-in fade-in-0 zoom-in-95">
        {items.length ? (
          items.map((item, index) => (
            <button
              key={index}
              className={cn(
                "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-left outline-none transition-colors",
                "hover:bg-accent hover:text-accent-foreground",
                "focus:bg-accent focus:text-accent-foreground",
                index === selectedIndex && "bg-accent text-accent-foreground"
              )}
              onClick={() => command(item)}
              type="button"
            >
              <item.icon className="h-4 w-4 shrink-0" />
              <div className="flex flex-col min-w-0">
                <span className="font-medium truncate">{item.title}</span>
                <span className="text-xs text-muted-foreground truncate">{item.description}</span>
              </div>
            </button>
          ))
        ) : (
          <div className="px-2 py-1.5 text-sm text-muted-foreground">No results</div>
        )}
      </div>
    );
  }
);

SlashCommandList.displayName = 'SlashCommandList';

const SlashCommandListContainer: React.FC<SlashCommandListContainerProps> = ({
  items,
  command,
  selectedIndex,
  clientRect,
}) => {
  const [position, setPosition] = React.useState<{ top: number; left: number } | null>(null);
  const listRef = React.useRef<any>(null);

  React.useEffect(() => {
    if (!clientRect) return;

    const updatePosition = () => {
      const rect = clientRect();
      if (rect) {
        setPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
        });
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [clientRect]);

  if (!position) return null;

  const content = (
    <div
      style={{
        position: 'absolute',
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 9999,
      }}
    >
      <SlashCommandList
        ref={listRef}
        items={items}
        command={command}
        selectedIndex={selectedIndex}
      />
    </div>
  );

  return createPortal(content, document.body);
};

export const SlashCommand = Extension.create({
  name: 'slashCommand',

  addOptions() {
    return {
      suggestion: {
        char: '/',
        command: ({ editor, range, props }: any) => {
          props.command({ editor, range });
        },
        items: getSuggestionItems,
        render: () => {
          let component: ReactRenderer;

          return {
            onStart: (props: any) => {
              component = new ReactRenderer(SlashCommandListContainer, {
                props: {
                  ...props,
                  clientRect: props.clientRect,
                },
                editor: props.editor,
              });
            },

            onUpdate(props: any) {
              component.updateProps({
                ...props,
                clientRect: props.clientRect,
              });
            },

            onKeyDown(props: any) {
              if (props.event.key === 'Escape') {
                return true;
              }

              // Access the ref through the component
              const ref = (window as any).__slashCommandRef;
              if (ref && ref.onKeyDown) {
                return ref.onKeyDown(props);
              }
              return false;
            },

            onExit() {
              component.destroy();
              delete (window as any).__slashCommandRef;
            },
          };
        },
      },
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ];
  },
});
